import { useCallback, useMemo, useState } from 'react';
import type { CustomFieldDefinition } from '../types/custom-field-definitions.types';
import {
  CONTACTS_TABLE_COLUMN_STORAGE_KEY,
  REQUIRED_CONTACTS_TABLE_COLUMNS,
  buildContactsTableColumnMap,
  buildContactsTableColumns,
  buildDefaultContactsColumnOrder,
  buildDefaultContactsColumnVisibility,
  type ContactsTableColumnDef,
  type ContactsTableColumnId,
} from '../config/contacts-table-columns';

type ContactsTableColumnConfig = {
  order: ContactsTableColumnId[];
  visible: Record<ContactsTableColumnId, boolean>;
};

const ensureRequiredColumnsFirst = (order: ContactsTableColumnId[]): ContactsTableColumnId[] => {
  const required = [...REQUIRED_CONTACTS_TABLE_COLUMNS];
  const rest = order.filter((id) => !required.includes(id as (typeof required)[number]));
  return [...required, ...rest];
};

const normalizeOrder = (
  order: string[],
  defaultOrder: ContactsTableColumnId[]
): ContactsTableColumnId[] => {
  const knownIds = new Set<ContactsTableColumnId>();
  const normalized: ContactsTableColumnId[] = [];

  order.forEach((id) => {
    if (
      defaultOrder.includes(id as ContactsTableColumnId) &&
      !knownIds.has(id as ContactsTableColumnId)
    ) {
      knownIds.add(id as ContactsTableColumnId);
      normalized.push(id as ContactsTableColumnId);
    }
  });

  defaultOrder.forEach((id) => {
    if (!knownIds.has(id)) {
      normalized.push(id);
    }
  });

  return ensureRequiredColumnsFirst(normalized);
};

const normalizeVisibility = (
  visible: Partial<Record<string, boolean>> | undefined,
  columns: ContactsTableColumnDef[],
  defaultVisibility: Record<ContactsTableColumnId, boolean>
): Record<ContactsTableColumnId, boolean> => {
  const normalized = { ...defaultVisibility };

  if (!visible) return normalized;

  columns.forEach((column) => {
    if (visible[column.id] !== undefined) {
      normalized[column.id] = Boolean(visible[column.id]);
    }
  });

  REQUIRED_CONTACTS_TABLE_COLUMNS.forEach((columnId) => {
    normalized[columnId] = true;
  });
  return normalized;
};

const loadStoredConfig = (
  defaultOrder: ContactsTableColumnId[],
  defaultVisibility: Record<ContactsTableColumnId, boolean>
): ContactsTableColumnConfig => {
  if (typeof window === 'undefined') {
    return {
      order: defaultOrder,
      visible: defaultVisibility,
    };
  }

  try {
    const raw = window.localStorage.getItem(CONTACTS_TABLE_COLUMN_STORAGE_KEY);
    if (!raw) {
      return {
        order: defaultOrder,
        visible: defaultVisibility,
      };
    }

    const parsed = JSON.parse(raw) as Partial<ContactsTableColumnConfig>;
    // Keep the stored choice raw: custom-attribute columns only exist once the field
    // definitions have loaded, so normalizing here would drop them. The hook normalizes
    // against the live columns when it derives what to render.
    return {
      order: Array.isArray(parsed.order) ? parsed.order : defaultOrder,
      visible: { ...defaultVisibility, ...(parsed.visible ?? {}) } as Record<
        ContactsTableColumnId,
        boolean
      >,
    };
  } catch {
    return {
      order: defaultOrder,
      visible: defaultVisibility,
    };
  }
};

const persistConfig = (config: ContactsTableColumnConfig) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(CONTACTS_TABLE_COLUMN_STORAGE_KEY, JSON.stringify(config));
};

export const useContactsTableColumns = (definitions: CustomFieldDefinition[] = []) => {
  const columns = useMemo(() => buildContactsTableColumns(definitions), [definitions]);
  const columnMap = useMemo(() => buildContactsTableColumnMap(definitions), [definitions]);
  const defaultOrder = useMemo(() => buildDefaultContactsColumnOrder(definitions), [definitions]);
  const defaultVisibility = useMemo(
    () => buildDefaultContactsColumnVisibility(definitions),
    [definitions]
  );

  const [config, setConfig] = useState<ContactsTableColumnConfig>(() =>
    loadStoredConfig(defaultOrder, defaultVisibility)
  );

  const normalizedConfig = useMemo(
    () => ({
      order: normalizeOrder(config.order, defaultOrder),
      visible: normalizeVisibility(config.visible, columns, defaultVisibility),
    }),
    [config.order, config.visible, columns, defaultOrder, defaultVisibility]
  );

  const orderedColumns = useMemo(
    () => normalizedConfig.order.map((id) => columnMap[id]).filter(Boolean),
    [normalizedConfig.order, columnMap]
  );

  const visibleColumns = useMemo(
    () => orderedColumns.filter((column) => normalizedConfig.visible[column.id]),
    [orderedColumns, normalizedConfig.visible]
  );

  const toggleableColumns = useMemo(
    () => orderedColumns.filter((column) => column.canHide),
    [orderedColumns]
  );

  const updateConfig = useCallback(
    (updater: (prev: ContactsTableColumnConfig) => ContactsTableColumnConfig) => {
      setConfig((prev) => {
        const current = {
          order: normalizeOrder(prev.order, defaultOrder),
          visible: {
            ...prev.visible,
            ...normalizeVisibility(prev.visible, columns, defaultVisibility),
          },
        };
        const next = updater(current);
        persistConfig(next);
        return next;
      });
    },
    [columns, defaultOrder, defaultVisibility]
  );

  const toggleColumnVisibility = useCallback(
    (columnId: ContactsTableColumnId, visible: boolean) => {
      const column = columnMap[columnId];
      if (!column?.canHide) return;

      updateConfig((prev) => ({
        ...prev,
        visible: { ...prev.visible, [columnId]: visible },
      }));
    },
    [columnMap, updateConfig]
  );

  const reorderColumns = useCallback(
    (activeId: ContactsTableColumnId, overId: ContactsTableColumnId) => {
      updateConfig((prev) => {
        const activeIndex = prev.order.indexOf(activeId);
        const overIndex = prev.order.indexOf(overId);
        if (activeIndex === -1 || overIndex === -1 || activeIndex === overIndex) {
          return prev;
        }

        const nextOrder = [...prev.order];
        nextOrder.splice(activeIndex, 1);
        nextOrder.splice(overIndex, 0, activeId);
        return { ...prev, order: ensureRequiredColumnsFirst(nextOrder) };
      });
    },
    [updateConfig]
  );

  const resetColumns = useCallback(() => {
    const next = {
      order: defaultOrder,
      visible: defaultVisibility,
    };
    setConfig(next);
    persistConfig(next);
  }, [defaultOrder, defaultVisibility]);

  return {
    orderedColumns,
    visibleColumns,
    toggleableColumns,
    columnVisibility: normalizedConfig.visible,
    toggleColumnVisibility,
    reorderColumns,
    resetColumns,
  };
};
